import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useAccount } from "../../Account";
import { useLanguage } from "../../Language";
import { useForm, SubmitHandler } from "react-hook-form";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import axios from "axios";
import { SuccessMessageControl } from "@tlc/ls2pac/app/controls/successMessageControl";

interface EditPINFormInput {
  pin: string;
  newPin: string;
  confirmNewPin: string;
}

interface HostSystemDiagImpl {
  methodSupported: boolean;
  validHostUser: boolean;
  validSessionUser: boolean;
  hostSystemFailure: boolean;
}

interface UpdatePinReturnImpl {
  hostSystemDiag: HostSystemDiagImpl;
  success: boolean;
}

// TODO min/max on new PIN field
export const EditPINPage: React.FC = () => {
  const { patron } = useAccount();
  const navigate = useNavigate();
  const { translate } = useLanguage();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<EditPINFormInput>({
    mode: "onBlur",
  });

  const [error, setError] = React.useState<string>();

  const onSubmit: SubmitHandler<EditPINFormInput> = async (data) => {
    setError(null);
    const { pin, newPin } = data;

    const { data: responseData } = await axios.post<UpdatePinReturnImpl>(
      `/account/update/pin`,
      {
        pid: patron.patronId,
        pin,
        newPin,
      }
    );

    if (responseData.success) {
      // Update Patron? Do we actually check the PIN value on the frontend?
      SuccessMessageControl.show(translate("successUpdatingPinMessage"));
      navigate("/myaccount/summary");
    } else {
      setError(translate("errorUpdatingPinMessage"));
    }
  };

  return (
    <>
      <Toolbar disableGutters={true}>
        <IconButton
          aria-label="back"
          onClick={() => navigate("/myaccount/summary")}
        >
          <ArrowBackIosIcon />
        </IconButton>
        <Typography variant="h5">
          {translate("changeYourLibraryPin")}
        </Typography>
      </Toolbar>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack gap={2} direction="column">
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label={translate("enterANewPin")}
            type="password"
            {...register("newPin", { required: translate("isRequired", {
              LABEL: translate("enterANewPin")
            }) })}
            required={true}
            helperText={errors.newPin?.message || " "}
            error={!!errors.newPin}
          />
          <TextField
            label={translate("confirmNewPin")}
            type="password"
            {...register("confirmNewPin", {
              required: translate("isRequired", {
                LABEL: translate("confirmNewPin"),
              }),
              validate: (value) =>
                value === watch("newPin") ||
                translate("newPINConfirmPINMustMatchError"),
            })}
            required={true}
            helperText={errors.confirmNewPin?.message || " "}
            error={!!errors.confirmNewPin}
          />
          <Divider />
          <TextField
            label={translate("oldPin")}
            type="password"
            {...register("pin", {
              required: translate("isRequired", {
                LABEL: translate("oldPin"),
              }),
            })}
            required={true}
            helperText={
              errors.pin?.message || translate("enterOldPinForSecurityPurposes")
            }
            error={!!errors.pin}
          />
          <Box>
            <Button type="submit" variant="contained">
              {translate("save")}
            </Button>
            <Button
              type="button"
              onClick={() => navigate("/myaccount/summary")}
            >
              {translate("cancel")}
            </Button>
          </Box>
        </Stack>
      </form>
    </>
  );
};
